import { faCircleNotch } from '@fortawesome/pro-light-svg-icons'
import { faCloudUpload } from '@fortawesome/pro-solid-svg-icons'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Spinner } from '../../../../App/Components'
import Button from '../../../../App/Components/Button/Button'
import { useAppDispatch } from '../../../../App/Hooks/reduxHooks'
import { getHrUsers } from '../../../../User/UsersHr/Store/Actions'

import Client from './Client/Client'
import { IAvatarData } from './Client/IAvatarData'
import {
  ButtonSpinner,
  ButtonsWrapper,
  DragFileElement,
  EditAvatarImage,
  EditAvatarOverlay,
  EditAvatarWrapper,
  Label,
  LabelText,
  SpinnerIcon,
  SpinnerWrapper,
  UploadIcon,
  UploadImageDropZone,
  UploadImageDropZoneWrapper,
  UploadImageMainContainer,
  UploadInput,
  UploadInputWrapper,
  UploadText,
  UploadTextWrapper,
} from './style'

type Props = {
  selectedPersonUuid: string
  setIsAvatarModalOpen: (isAvatarModalOpen: boolean) => void
  setPersonCrud: (resetState: null) => void
}

export const AvatarEdit: React.FC<Props> = ({
  selectedPersonUuid,
  setIsAvatarModalOpen,
  setPersonCrud,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [images, setImages] = useState<File[]>([])
  const [dragActive, setDragActive] = useState<boolean>(false)
  const [imageURLs, setImageURLs] = useState<string[]>([])
  const [imageData, setImageData] = useState<IAvatarData[]>([])
  const [isAvatarsLoading, setIsAvatarsLoading] = useState<boolean>(false)
  const [selectedAvatarUuid, setSelectedAvatarUuid] = useState<string>('')
  const [isAvatarRequestLoading, setIsAvatarRequestLoading] =
    useState<boolean>(false)
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] =
    useState<boolean>(false)
  const [isSelected, setIsSelected] = useState<boolean>(false)
  const [isErrorMessageVisible, setIsErrorMessageVisible] =
    useState<boolean>(false)
  const [isSuccessMessageVisible, setIsSuccessMessageVisible] =
    useState<boolean>(false)

  useEffect(() => {
    setIsAvatarsLoading(true)
    Client.getAvatars(selectedPersonUuid).then(response => {
      setImageData(response)
      setIsAvatarsLoading(false)
      const activeAvatar = response.find(
        (avatar: IAvatarData) => avatar.isActive,
      )
      if (activeAvatar) {
        setSelectedAvatarUuid(activeAvatar.uuid)
      }
    })
  }, [selectedPersonUuid])

  useEffect(() => {
    if (images.length < 1) return
    const newImageUrls: string[] = []
    images.forEach(image => newImageUrls.push(URL.createObjectURL(image)))
    setImageURLs(newImageUrls)
  }, [images])

  const handleDropFile = (files: any) => {
    setImages([...files])
    setIsSelected(false)
  }

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSelected(false)
    if (e.currentTarget.files) {
      setImages(Array.from(e.currentTarget.files))
    }
    if (e.currentTarget.files && e.currentTarget.files[0]) {
      handleDropFile(e.currentTarget.files)
    }
  }

  const handleDrag = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    setIsSelected(false)
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    setIsSelected(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleDropFile(e.dataTransfer.files)
    }
  }

  const handleAvatarSelect = (uuid: string) => {
    const updatedImageData = imageData.map(image => {
      return image.uuid === uuid
        ? { ...image, isActive: true }
        : { ...image, isActive: false }
    })
    setImageData(updatedImageData)
    setSelectedAvatarUuid(uuid)
    setIsSelected(true)
  }

  const setSuccessMessageAndTimeout = () => {
    setIsSuccessMessageVisible(true)
    setTimeout(() => {
      setIsSuccessMessageVisible(false)
    }, 3000)
  }

  const handleAvatarResponse = () => {
    dispatch(getHrUsers())
    setIsSaveButtonDisabled(false)
    setIsAvatarRequestLoading(false)
    setIsAvatarModalOpen(false)
    setPersonCrud(null)
    setIsSelected(false)
  }

  const saveSelectedAvatar = () => {
    setIsAvatarRequestLoading(true)
    setIsSaveButtonDisabled(true)
    if (isSelected) {
      Client.changeAvatars(selectedPersonUuid, selectedAvatarUuid).then(
        response => {
          if (response.status === 'success') {
            setSuccessMessageAndTimeout()
            setTimeout(handleAvatarResponse, 3000)
          }
        },
      )
    } else if (!isSelected && images.length > 0) {
      Client.uploadAvatar(selectedPersonUuid, images).then(response => {
        if (response.status === 'success') {
          setSuccessMessageAndTimeout()
          setTimeout(handleAvatarResponse, 3000)
        }
      })
    } else {
      setIsErrorMessageVisible(true)
      setIsAvatarRequestLoading(false)
      setTimeout(() => {
        setIsErrorMessageVisible(false)
      }, 5000)
    }
  }

  return (
    <>
      {isAvatarsLoading ? (
        <Spinner />
      ) : (
        <EditAvatarWrapper rowLength={imageData.length}>
          {imageData.map(image => (
            <div key={image.id} style={{ position: 'relative' }}>
              <EditAvatarImage
                key={image.id}
                src={image.url}
                active={image.isActive}
              />
              {!image.isActive && (
                <EditAvatarOverlay
                  onClick={() => handleAvatarSelect(image.uuid)}
                />
              )}
            </div>
          ))}
        </EditAvatarWrapper>
      )}
      <hr />
      <form onDragEnter={e => handleDrag(e)} onSubmit={e => e.preventDefault()}>
        <UploadImageMainContainer>
          <UploadImageDropZoneWrapper>
            <UploadImageDropZone>
              <UploadTextWrapper>
                <UploadIcon icon={faCloudUpload} />
                <UploadText>{t('Drag & drop to upload a file')}</UploadText>
                <UploadText>{t('or')}</UploadText>
              </UploadTextWrapper>
              <UploadInputWrapper>
                <UploadInput
                  type="file"
                  multiple={false}
                  accept=".jpg,.png,.gif"
                  onChange={e => onImageChange(e)}
                />
              </UploadInputWrapper>
            </UploadImageDropZone>
          </UploadImageDropZoneWrapper>
        </UploadImageMainContainer>
      </form>
      {dragActive && (
        <DragFileElement
          onDragEnter={e => handleDrag(e)}
          onDragLeave={e => handleDrag(e)}
          onDragOver={e => handleDrag(e)}
          onDrop={e => handleDrop(e)}
        />
      )}

      {isErrorMessageVisible && (
        <Label color={'error'}>
          <LabelText> {t(`You haven't selected any image`)}</LabelText>
        </Label>
      )}

      {isSuccessMessageVisible && (
        <Label color={'success'}>
          <LabelText> {t(`Image successfully updated`)}</LabelText>
        </Label>
      )}

      <ButtonsWrapper>
        <Button>{t('Cancel')} </Button>
        {isAvatarRequestLoading ? (
          <ButtonSpinner>
            <SpinnerWrapper>
              <SpinnerIcon icon={faCircleNotch} />
            </SpinnerWrapper>
          </ButtonSpinner>
        ) : (
          <Button disabled={isSaveButtonDisabled} onClick={saveSelectedAvatar}>
            {t('Save')}
          </Button>
        )}
      </ButtonsWrapper>
    </>
  )
}
